import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DatePicker, DatePickerInput, Toggle } from 'carbon-components-react';
import Utils from 'utilities/helper.util';
import dayjs from 'dayjs';
import { Help16 } from '@carbon/icons-react';
import FormError from 'components/FormError/FormError';
import { CustomTooltipDefinition } from 'components';

const AutoRenewComponent: React.FC<Props> = ({
  readOnlyMode,
  jobEndDate,
  autoRenewUntil,
  autoRenewToggle,
  autoRenewUntilErrors,
  updateValues,
  autoRenewTooltipEnabled
}: any) => {
  const NEXT_SIX_MOTNHS: any = Utils.getDateAfterMonths(6);
  const NEXT_28_DAYS_DATE: any = Utils.getDateAfterDays(28);

  const [showAutoRenewDate, setShowAutoRenewDate] = useState<boolean>(false);
  const defaultToggled = autoRenewToggle && autoRenewUntil !== null;
  const toggleHandler = (val: boolean) => {
    setShowAutoRenewDate(val);
    updateValues(val);
  };

  const getAutoRenewDate = (selectedDate: any) => {
    const isValidDate = Utils.isDateAfterExtMonth(selectedDate, 6);
    let _date = isValidDate ? NEXT_SIX_MOTNHS : selectedDate;
    _date = _date === 'Invalid Date' ? '' : _date;
    return _date;
  };

  const onCalendarClose = (val: any, strValue: any, self: any) => {
    strValue && self.setDate(strValue, true, 'd/m/Y');
  };

  useEffect(() => {
    setShowAutoRenewDate(defaultToggled);
  }, [defaultToggled]);

  return (
    <section className="formFieldItem autoRenew--wrapper">
      <div className="autoRenew--selection">
        {autoRenewTooltipEnabled ? (
          <CustomTooltipDefinition
            tooltipText="For longer jobs you have the option to automatically renew and get fresh plans every 28 days"
            className="autoRenew--toolTip"
          >
            Auto-renew
          </CustomTooltipDefinition>
        ) : (
          <span className="autoRenew--text">Auto-renew</span>
        )}
        <Toggle
          aria-label="Auto renew toggle button"
          defaultToggled={defaultToggled}
          id="autoRenewToggle"
          name="autoRenewToggle"
          className="autoRenew--toggle"
          onToggle={(e: boolean) => toggleHandler(e)}
          disabled={readOnlyMode}
        />
      </div>
      {(showAutoRenewDate ||
        !dayjs(NEXT_28_DAYS_DATE).isBefore(dayjs(jobEndDate))) && (
        <div className="autoRenew--expanded">
          <CustomTooltipDefinition
            tooltipText="The maximum auto-renew period is 6 months from today (a new enquiry is needed after that). Set a shorter period to avoid receiving plans you won’t need."
            className="autoRenew--toolTip"
          >
            Auto-renew until
          </CustomTooltipDefinition>
          <DatePicker
            dateFormat="d/m/Y"
            datePickerType="single"
            className="autoRenew--until"
            minDate={Utils.toLocalDateFormat(NEXT_28_DAYS_DATE)}
            maxDate={
              dayjs(jobEndDate).isBefore(dayjs(NEXT_SIX_MOTNHS))
                ? Utils.toLocalDateFormat(jobEndDate)
                : Utils.toLocalDateFormat(NEXT_SIX_MOTNHS)
            }
            onChange={(val: any) => updateValues(val)}
            value={
              autoRenewUntil !== ''
                ? Utils.toLocalDateFormat(autoRenewUntil)
                : autoRenewUntil === null
                ? ''
                : Utils.toLocalDateFormat(getAutoRenewDate(jobEndDate))
            }
            onClose={onCalendarClose}
          >
            <DatePickerInput
              id="autoRenewUntil"
              placeholder="dd/mm/yyyy"
              labelText=""
              type="text"
              name="autoRenewUntil"
              data-testid="autoRenewUntil"
              disabled={readOnlyMode}
            />
          </DatePicker>
          {autoRenewUntilErrors !== '' && (
            <FormError>{autoRenewUntilErrors}</FormError>
          )}
        </div>
      )}

      <p
        className="autoRenew--info"
        onClick={() =>
          window.open(
            'https://help.byda.com.au/article/enquiry-auto-renew/',
            '_blank'
          )
        }
      >
        <Help16 />
        Learn more about the auto-renew option
      </p>
    </section>
  );
};

const propTypes = {
  readOnlyMode: PropTypes.bool,
  jobEndDate: PropTypes.any.isRequired,
  autoRenewUntil: PropTypes.any,
  autoRenewToggle: PropTypes.bool,
  updateValues: PropTypes.func.isRequired,
  autoRenewUntilErrors: PropTypes.any,
  autoRenewTooltipEnabled: PropTypes.bool
};

type Props = PropTypes.InferProps<typeof propTypes>;

AutoRenewComponent.propTypes = propTypes;

AutoRenewComponent.defaultProps = {
  readOnlyMode: false,
  jobEndDate: '',
  autoRenewUntil: '',
  autoRenewToggle: false,
  updateValues: () => {},
  autoRenewUntilErrors: '',
  autoRenewTooltipEnabled: true
};

export default AutoRenewComponent;
